import { useCallback, useState } from "react";
import axios from "axios";
import clsx from "clsx";
import Cookies from "js-cookie";
import { TailSpin } from "react-loader-spinner";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { ApprovalDashboardStats } from "../../components/features/approval-dashboard/components/approval-dashboard-stats/approval-dashboard-stats.tsx";
import { ApprovalDashboardFilters } from "../../components/features/approval-dashboard/components/approval-dashboard-filters/approval-dashboard-filters.tsx";
import { ApprovalDashboardTable } from "../../components/features/approval-dashboard/components/approval-dashboard-table/approval-dashboard-table.tsx";
import { useApprovalDashboard } from "../../components/features/approval-dashboard/hooks/use-approval-dashboard.ts";
import { useApprovalDashboardStats } from "../../components/features/approval-dashboard/hooks/use-approval-dashboard-stats.ts";
import { useExportApprovalDashboard } from "../../components/features/approval-dashboard/hooks/use-export-approval-dashboard.ts";
import { DEFAULT_STATUS_FILTER } from "../../components/features/approval-dashboard/constants.ts";
import type {
  ApprovalDashboardFilters as Filters,
  ApprovalEmployeeRow,
} from "../../components/features/approval-dashboard/types.ts";
// @ts-ignore - JS module
import ApprovalDashboardActionModal from "../../components/features/approval-dashboard/modals/approve-timesheet-modal.jsx";
import { GET_WFH_TIMESHEETS } from "../../../config/Endpoints.js";
import { showError } from "../../utils/toast.ts";

interface ApprovalDashboardPageProps {
  title?: string;
  description?: string;
  
}

const INITIAL_FILTERS: Filters = {
  status: DEFAULT_STATUS_FILTER,
  approverId: null,
  employeeId: null,
  region: null,
  fromDate: null,
  toDate: null,
};

export default function ApprovalDashboardPage({
  title,
  description,
}: ApprovalDashboardPageProps) {
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(5);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewTimesheet, setViewTimesheet] = useState<unknown[]>([]);
  const [viewSelectedRequest, setViewSelectedRequest] = useState<unknown>(null);

  const { stats, loading: statsLoading, refetch: refetchStats } =
    useApprovalDashboardStats(filters);
  const {
    groups,
    totalElements,
    loading: tableLoading,
    pageNumber,
    pageSize,
    isLastPage,
    isFirstPage,
    refetch: refetchDashboard,
  } = useApprovalDashboard(filters, page, size);
  const {
    exportApprovalDashboard,
    exportTimesheetFillingReport,
    exportEmployeeProjectTimeReport,
    exporting,
  } = useExportApprovalDashboard();

  const handleApply = useCallback((next: Filters) => {
    setFilters(next);
    setPage(1);
  }, []);

  const handleReset = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  }, []);

  const handleExport = useCallback((
    report: "approval" | "filling" | "projectTime",
    selectedFilters: Filters,
  ) => {
    if (report === "projectTime") {
      void exportEmployeeProjectTimeReport(selectedFilters);
      return;
    }
    if (report === "filling") {
      void exportTimesheetFillingReport(selectedFilters);
      return;
    }
    void exportApprovalDashboard(selectedFilters);
  }, [exportApprovalDashboard, exportEmployeeProjectTimeReport, exportTimesheetFillingReport]);

  const handlePageSizeChange = useCallback((nextSize: number) => {
    setSize(nextSize);
    setPage(1);
  }, []);

  const handleView = useCallback(async (row: ApprovalEmployeeRow) => {
    const timesheetId = row.timesheetId ?? row.id;
    if (timesheetId == null) {
      showError("Timesheet id is missing for this row.");
      return;
    }

    const accessToken = Cookies.get("accessToken");
    setViewLoading(true);
    try {
      const res = await axios.get(GET_WFH_TIMESHEETS, {
        params: { id: timesheetId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      const data = res.data;
      const ts = Array.isArray(data)
        ? data[0]
        : Array.isArray(data?.content)
          ? data.content[0]
          : data;
      if (!ts) {
        showError("Timesheet not found.");
        return;
      }
      const entries = Array.isArray(ts.entries) ? ts.entries : [];
      setViewTimesheet(entries);
      setViewSelectedRequest(ts);
      setViewModalOpen(true);
    } catch (err) {
      const msg =
        (axios.isAxiosError(err) && err.response?.data?.message) ||
        (err instanceof Error ? err.message : null) ||
        "Unable to load timesheet details.";
      showError(msg);
    } finally {
      setViewLoading(false);
    }
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setViewSelectedRequest(null);
    setViewTimesheet([]);
  }, []);

  const handleActionComplete = useCallback(() => {
    setViewModalOpen(false);
    setViewSelectedRequest(null);
    setViewTimesheet([]);
    void refetchDashboard();
    void refetchStats();
  }, [refetchDashboard, refetchStats]);

  return (
    <PageShell>
      {title || description ? (
        <div className="mb-3">
          {title ? (
            <h1 className={clsx("heading-md", "mb-1")}>{title}</h1>
          ) : null}
          {description ? (
            <p className={clsx("body-sm", "text-muted", "mb-0")}>
              {description}
            </p>
          ) : null}
        </div>
      ) : null}

      <ApprovalDashboardStats stats={stats} loading={statsLoading} />

      <ApprovalDashboardFilters
        value={filters}
        onApply={handleApply}
        onReset={handleReset}
        onExport={handleExport}
        exporting={exporting}
        loading={tableLoading}
      />

      <ApprovalDashboardTable
        groups={groups}
         filters={filters}
        statusFilter={filters.status}
        loading={tableLoading}
        page={page}
        size={size}
        pageNumber={pageNumber}
        pageSize={pageSize}
        totalElements={totalElements}
        isLastPage={isLastPage}
        isFirstPage={isFirstPage}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        onView={handleView}
      />

      {viewLoading ? (
        <div className="overlay">
          <div className="loader-container">
            <TailSpin
              height="100"
              width="100"
              radius="1"
              color="blue"
              ariaLabel="loading-timesheet"
              visible
            />
          </div>
        </div>
      ) : null}

      <ApprovalDashboardActionModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        timesheet={viewTimesheet}
        selectedRequest={viewSelectedRequest}
        onActionComplete={handleActionComplete}
      />
    </PageShell>
  );
}
